// Local cache of group chats. It is only a cache: the server is the source of truth,
// so every write is best-effort and bounded to stay inside the localStorage quota.
const CHATS_KEY = 'chats';
const LIST_KEY = 'chatList';
const MAX_CACHED_MESSAGES = 400;
const MAX_CACHED_CHATS = 30;

const read = (key, fallback) => {
    try {
        return JSON.parse(localStorage.getItem(key)) || fallback;
    } catch (_) {
        return fallback;
    }
};

const write = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (_) {
        // Quota exceeded: drop the cache rather than break the chat.
        try {
            localStorage.removeItem(key);
        } catch (__) {}
    }
};

// Keep the newest messages; first_message_id must then point at the oldest one kept.
const trim = (chat) => {
    if (!chat || !Array.isArray(chat.messages) || chat.messages.length <= MAX_CACHED_MESSAGES) {
        return chat;
    }
    const messages = chat.messages.slice(-MAX_CACHED_MESSAGES);
    return { ...chat, messages, first_message_id: messages[0]?.id ?? chat.first_message_id };
};

const saveChats = (chats) => {
    write(CHATS_KEY, chats.filter(Boolean).slice(-MAX_CACHED_CHATS).map(trim));
};

const setChatList = (chats) => {
    write(LIST_KEY, chats);
}

const getChatList = () => {
    return read(LIST_KEY, []);
}

const syncChat = (chat) => {
    if (!chat?.id) return;
    const chats = read(CHATS_KEY, []).filter(c => c?.id != chat.id);
    chats.push(chat);
    saveChats(chats);
}

const pushMoreChat = (chat) => {
    const chats = read(CHATS_KEY, []);
    const existingChatIndex = chats.findIndex(c => c?.id == chat?.id);

    if (existingChatIndex > -1) {
        const existingChat = chats[existingChatIndex];
        const known = new Set((chat.messages || []).map(m => m?.id));
        chat.messages = [...(chat.messages || []), ...(existingChat.messages || []).filter(m => !known.has(m?.id))];
        chats[existingChatIndex] = chat;
    }

    // Older pages are kept in memory for this view but not all of them in storage.
    saveChats(chats);
    return chat;
}

const getFirstChat = () => {
    const chats = read(CHATS_KEY, []);
    return chats.length > 0 ? chats[0] : null;
}

const getChat = (chatId) => {
    const chats = read(CHATS_KEY, []);
    return chats.find(chat => chat?.id == chatId);
}

const deleteChat = (chatId) => {
    const chats = read(CHATS_KEY, []);
    saveChats(chats.filter(chat => chat?.id != chatId));
}

// Insert or replace one message, keeping ascending id order.
const syncChatMessages = (chatId, message) => {
    if (!message?.id) return;
    const chats = read(CHATS_KEY, []);
    const chat = chats.find(chat => chat?.id == chatId);

    if (!chat) return;

    const messages = chat.messages || [];
    const messageIndex = messages.findIndex(m => m?.id === message.id);

    if (messageIndex > -1) {
        messages[messageIndex] = message;
    } else {
        let inserted = false;
        for (let i = 0; i < messages.length; i++) {
            if (message.id < messages[i]?.id) {
                messages.splice(i, 0, message);
                inserted = true;
                break;
            }
        }
        if (!inserted) {
            messages.push(message);
        }
    }

    chat.messages = messages;
    saveChats(chats);
};

const markOwnMessages = (chatId, userId, messageIds, field) => {
    const localChat = getChat(chatId);
    if (!localChat?.messages) return;
    localChat.messages = localChat.messages.map(message => {
        const mine = message.sender?.id == userId || message.user_id == userId;
        if (mine && !message[field] && messageIds?.includes(message?.id)) {
            return { ...message, [field]: true };
        }
        return message;
    });
    syncChat(localChat);
};

const DeliveredToAll = (chats, userId) => {
    (chats || []).forEach(chat => markOwnMessages(chat?.id, userId, chat?.message_ids, 'delivered_to_all'));
}

const SeenByAll = (chat, userId) => {
    markOwnMessages(chat?.id, userId, chat?.message_ids, 'seen_by_all');
}

const clearChats = () => {
    localStorage.removeItem(CHATS_KEY);
    localStorage.removeItem(LIST_KEY);
}

export { clearChats, deleteChat, DeliveredToAll, getChat, getChatList, getFirstChat, pushMoreChat, SeenByAll, setChatList, syncChat, syncChatMessages };
